import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  AbstractControlOptions,
  FormControl,
  FormGroup,
  ValidationErrors,
  ValidatorFn,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { BehaviorSubject, Observable, of, Subject } from 'rxjs';
import { filter, takeUntil, tap } from 'rxjs/operators';
import { CertificateBruger, Postadresse } from 'src/app/api/models';
import { ComponentCanDeactivateDirective } from 'src/app/core/components/component-can-deactivate';
import { FormHelper } from 'src/app/core/helpers/form-helper';
import { PostAdressePipe } from 'src/app/core/pipes/post-adresse.pipe';
import { TekstResourcePipe } from 'src/app/core/pipes/tekstresource.pipe';
import { AuthService } from 'src/app/core/services/auth/auth.service';
import { SaveOperationsService } from 'src/app/core/services/operations/save/save-operations.service';
import { ProfilAdresse } from '../../models/profil-adresse.model';
import { CurrentProfilService } from '../../services/current-profil.service';

enum FormControlNames {
  Navn = 'navn',
  Id = 'id',
  Adresse = 'adresse',
  Landkode = 'landkode',
  NoEmailCheckbox = 'noEmailCheckbox',
  Email = 'email',
  Telefon = 'telefon',
  Cvr = 'cvr',
}

enum FormErrorKeys {
  Kontaktoplysninger_Kontaktform_IkkeValid = 'Kontaktoplysninger.Kontaktform.IkkeValid',
  Kontaktoplysninger_Email_IkkeValid = 'Kontaktoplysninger.Email.IkkeValid',
  Kontaktoplysninger_DKTelefon_IkkeValid = 'Kontaktoplysninger.DKTelefon.IkkeValid',
  Kontaktoplysninger_Telefon_IkkeValid = 'Kontaktoplysninger.Telefon.IkkeValid',
}

enum TekstResourceKeys {
  Kontaktoplysninger_Telefon_Hint = 'Kontaktoplysninger.Telefon.Hint',
  Kontaktoplysninger_Id_Hint = 'Kontaktoplysninger.Id.Hint',
  Kontaktoplysninger_Id_Copy = 'Kontaktoplysninger.Id.Copy',
  Kontaktoplysninger_Email_Hint = 'Kontaktoplysninger.Email.Hint',
  Kontaktoplysninger_NoEmailCheckbox = 'Kontaktoplysninger.NoEmailCheckbox',
}

@Component({
  selector: 'bom-kontaktoplysninger',
  templateUrl: './kontaktoplysninger.component.html',
  styleUrls: ['./kontaktoplysninger.component.scss'],
})
export class KontaktoplysningerComponent
  extends ComponentCanDeactivateDirective
  implements OnInit, OnDestroy {
  @Input()
  showWorkspaceTitle = true;

  isDiscardPendingChangesModalOpen$ = new BehaviorSubject<boolean>(false);
  flagDiscardPendingChanges$ = new BehaviorSubject<boolean>(null);
  componentName = 'Navn og kontaktoplysninger';
  readonly FormControlNames = FormControlNames;
  readonly FormErrorKeys = FormErrorKeys;
  readonly TekstResrouceKeys = TekstResourceKeys;

  landkoder: Landkoder[] = [
    { text: '(Danmark) +45', code: '45' },
    { text: '(Norge) +47', code: '47' },
    { text: '(Sverige) +46', code: '46' },
    { text: '(Tyskland) +49', code: '49' },
  ];
  denmarkLandKode: Landkoder = this.landkoder[0];
  componentDestroyed$ = new Subject<any>();
  selectedLandkode: Landkoder;
  displayAddress: string;
  noAddressFound = false;
  projektId = '';
  sagsId = '';
  form: FormGroup = new FormGroup(
    {
      [FormControlNames.Navn]: new FormControl('', [Validators.required]),
      [FormControlNames.Id]: new FormControl('', [Validators.required]),
      [FormControlNames.Adresse]: new FormControl('', {
        validators: [Validators.required, Validators.minLength(9)],
        updateOn: 'change',
      } as AbstractControlOptions),
      [FormControlNames.Landkode]: new FormControl('', {
        validators: [this.landValidator()],
        updateOn: 'change',
      } as AbstractControlOptions),
      [FormControlNames.Telefon]: new FormControl('', {
        validators: [Validators.required, this.telefonValidator()],
        updateOn: 'blur',
      }),
      [FormControlNames.Email]: new FormControl('', {
        validators: [Validators.pattern(FormHelper.EmailPattern), this.emailValidator()],
        updateOn: 'blur',
      }),
      [FormControlNames.NoEmailCheckbox]: new FormControl(false, {
        updateOn: 'change',
      }),
      [FormControlNames.Cvr]: new FormControl(''),
    },
    { updateOn: 'blur' }
  );

  private isSaving: boolean;
  private get navnField(): AbstractControl {
    return this.form.get(FormControlNames.Navn);
  }
  private get brugerIdField(): AbstractControl {
    return this.form.get(FormControlNames.Id);
  }
  private get emailField(): AbstractControl {
    return this.form.get([FormControlNames.Email]);
  }
  private get noEmailCheckbox(): AbstractControl {
    return this.form.get([FormControlNames.NoEmailCheckbox]);
  }
  private get phoneField(): AbstractControl {
    return this.form.get([FormControlNames.Telefon]);
  }
  private get landKode(): AbstractControl {
    return this.form.get([FormControlNames.Landkode]);
  }
  private get adresse(): AbstractControl {
    return this.form.get(FormControlNames.Adresse);
  }

  constructor(
    public currentProfilService: CurrentProfilService,
    public authService: AuthService,
    private saveOperationsService: SaveOperationsService,
    private postAdressePipe: PostAdressePipe,
    private route: ActivatedRoute,
    private router: Router,
    public tekstResource: TekstResourcePipe
  ) {
    super();
  }

  ngOnInit() {
    this.currentProfilService.getProfilInformation();
    this.setupHandleSaveOperationChanges();
    this.setupHandleProfileInformationChanges();
    this.setupQueryParamsChanges();
  }

  ngOnDestroy() {
    // Manually blur to trigger change detection in ReactiveForms
    if (document.activeElement) {
      (document.activeElement as any).blur();
    }
    this.componentDestroyed$.next();
    this.componentDestroyed$.complete();
  }

  /**
   * Fired when address is selected in Profil-Address-input component. Updates the profile address
   * @param profilAdresse
   */
  onAddressSelected(profilAdresse: ProfilAdresse) {
    this.noAddressFound = false;
    const updatedProfil: CertificateBruger = {
      ...this.currentProfilService.profilInformation$.value,
      adresse: profilAdresse.postAdresse,
      indtastetAdresse: profilAdresse.indtastetAdresse,
    };
    this.currentProfilService.profilInformation$.value.adresse = profilAdresse.postAdresse;
    this.currentProfilService.profilInformation$.value.indtastetAdresse =
      profilAdresse.indtastetAdresse;
  }

  /**
   * Event triggered when user selects an address
   * @param adresse
   */
  onPostAdresse(adresse: Postadresse) {
    if (adresse) {
      this.noAddressFound = false;
      const profilAdresse = <ProfilAdresse>{
        postAdresse: adresse,
        indtastetAdresse: this.postAdressePipe.transform(adresse),
      };
      this.onAddressSelected(profilAdresse);
    } else {
      this.noAddressFound = true;
    }
  }

  onNoEmailCheckboxChange(checked: boolean): void {
    const emailCtrl = this.form.get(this.FormControlNames.Email);

    if (checked) {
      emailCtrl.disable({ emitEvent: false });
      emailCtrl.setErrors(null);
      emailCtrl.setValue('');
    } else {
      emailCtrl.enable({ emitEvent: false });
    }

    // Revalidates Email, when marking the checkbox
    emailCtrl.updateValueAndValidity({ emitEvent: true });
  }

  landValidator(): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      // Validator working on instance of component, and not on formgroup parameter,
      // because validation is performed across 2 form elements, and nesting formgroups is messy when moving elements around

      // on component init, form is null, so need special init handling
      if (this.form === null || this.form === undefined) {
        return null;
      }

      // Changes to "land" control affect "telefon" validation
      this.phoneField.updateValueAndValidity({ emitEvent: true });

      return null;
    };
  }

  emailValidator(): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      // Validator working on instance of component, and not on formgroup parameter,
      // because validation is performed across 2 form elements, and nesting formgroups is messy when moving elements around

      // on component init, form is null, so need special init handling
      if (this.form === null || this.form === undefined) {
        return null;
      }

      let error: ValidationErrors | null = null;

      // If user has an email, the emailField can't be empty
      // There is a validator on the FormControl that verify that the email is a correct email
      if (!this.noEmailCheckbox.value && !this.emailField.value) {
        error = {
          message: this.tekstResource.transform(FormErrorKeys.Kontaktoplysninger_Email_IkkeValid),
        };
      }

      return error;
    };
  }

  telefonValidator(): ValidatorFn {
    return (control: FormGroup): ValidationErrors | null => {
      // Validator working on instance of component, and not on formgroup parameter,
      // because validation is performed across 2 form elements, and nesting formgroups is messy when moving elements around

      // on component init, form is null, so need special init handling
      if (this.form === null || this.form === undefined) {
        return null;
      }

      let error: ValidationErrors | null = null;

      if (
        this.landKode.valid &&
        this.landKode.value?.code === this.denmarkLandKode.code &&
        !FormHelper.isTelefonValid(this.phoneField.value)
      ) {
        error = {
          message: this.tekstResource.transform(
            FormErrorKeys.Kontaktoplysninger_DKTelefon_IkkeValid
          ),
        };
      }

      return error;
    };
  }

  saveProfile() {
    const profil = this.getProfilFromForm();
    this.updateIfValid(profil);
  }

  returnToIndsend() {
    const paths = [
      'projekter',
      'fortsaet',
      this.projektId,
      'sag',
      this.sagsId,
      'indsend',
      'fase',
      'Udfyld ansøgning',
    ];
    this.router.navigate(paths);
  }

  private getProfilFromForm() {
    const user = this.currentProfilService.profilInformation$.value;

    return <CertificateBruger>{
      ...user,
      adresse: user.adresse,
      email: (this.emailField.value as string)?.trim(),
      indtastetAdresse: user.adresse
        ? this.postAdressePipe.transform(user.adresse)
        : this.adresse.value,
      harIkkeEmailAdresse: this.noEmailCheckbox.value,
      navn: this.navnField.value,
      brugerId: this.brugerIdField.value,
      telefon: this.phoneField.value,
      telefonLandekode: this.landKode.valid ? this.landKode.value.code : this.denmarkLandKode.code,
    };
  }

  private setupHandleProfileInformationChanges() {
    this.currentProfilService.profilInformation$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(profilInfo => {
        this.patchValuesInForm(profilInfo);
        this.form.markAsPristine();
      });
  }

  private setupHandleSaveOperationChanges() {
    this.saveOperationsService.isOperating$
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(isSaving => (this.isSaving = isSaving));
  }

  private setupQueryParamsChanges() {
    this.route.queryParams // (stop 1-line format on save)
      .pipe(takeUntil(this.componentDestroyed$))
      .subscribe(params => {
        this.projektId = params['projektId'] ?? '';
        this.sagsId = params['sagsId'] ?? '';
      });
  }

  /**
   * Patch values to from after getting data from API
   */
  private patchValuesInForm(profil: CertificateBruger) {
    if (profil) {
      // Disables the control for address, to avoid validation checks on patchValue(profil)
      // since address (postAddress) can be null
      this.adresse.disable({ emitEvent: false });

      // Only root form controls are updated, not nested form groups (like kontaktMuligheder)
      this.form.patchValue(profil, { emitEvent: false });

      if (!this.authService.isCitizen$.value && this.authService.userInfo$) {
        this.form
          .get(FormControlNames.Cvr)
          .setValue(this.authService.userInfo$.value.cvr, { emitEvent: false });
        this.form.get(FormControlNames.Cvr).disable({ emitEvent: false });
        this.form.get(FormControlNames.Navn).disable({ emitEvent: false });
        this.form.get(FormControlNames.Id).disable({ emitEvent: false });
      } else {
        this.form.get(FormControlNames.Navn).disable({ emitEvent: false });
        this.form.get(FormControlNames.Id).disable({ emitEvent: false });
      }
      this.setInitialKontaktformState(profil);
      this.onNoEmailCheckboxChange(this.form.get(this.FormControlNames.NoEmailCheckbox).value);

      // Enables the validation where the value for address actually gets patched
      this.adresse.enable({ emitEvent: false });

      this.form
        .get(FormControlNames.Adresse)
        .patchValue(
          (this.displayAddress = profil.indtastetAdresse
            ? profil.indtastetAdresse
            : this.postAdressePipe.transform(profil.adresse)),
          { emitEvent: false }
        );
    }
  }

  private setInitialKontaktformState(profil: CertificateBruger): void {
    // Set checkboxes state based on profile info
    this.noEmailCheckbox.setValue(profil.harIkkeEmailAdresse, { emitEvent: false });
    this.emailField.setValue(profil.email, { emitEvent: false });
    this.phoneField.setValue(profil.telefon, { emitEvent: false });
    if (profil.telefonLandekode !== null) {
      this.landKode.patchValue(this.getLandkode(profil.telefonLandekode), { emitEvent: false });
    } else {
      this.landKode.patchValue(this.denmarkLandKode, { emitEvent: false });
    }
  }

  /**
   * Set landkode model on landkode dropdown
   * @param landkode
   */
  private getLandkode(landkode: string): Landkoder {
    return this.landkoder.find(l => l.code === landkode);
  }

  private updateIfValid(profil: CertificateBruger): void {
    if (this.form.valid && !this.adresse.dirty) {
      this.currentProfilService.updateProfilInformation(profil);
      this.form.markAsPristine();
    }
  }

  onIgnorePendingChange(discardChanges: boolean): void {
    this.flagDiscardPendingChanges$.next(discardChanges);
    this.isDiscardPendingChangesModalOpen$.next(false);
  }

  warnOnUnload(): boolean {
    const dirtyFields = this.emailField.dirty || this.phoneField.dirty || this.adresse.dirty;
    return this.isSaving || dirtyFields;
  }

  canDeactivate(): Observable<boolean> {
    const dirtyFields = this.emailField.dirty || this.phoneField.dirty || this.adresse.dirty;

    if (dirtyFields) {
      this.flagDiscardPendingChanges$.next(null);
      this.isDiscardPendingChangesModalOpen$.next(true);
      return this.flagDiscardPendingChanges$.pipe(
        filter(discard => discard !== null),
        tap(x => {
          if (x) {
            this.form.markAsPristine();
          }
        })
      );
    } else {
      return of(true);
    }
  }

  copyToClipboard(textToCopy: string): void {
    navigator.clipboard.writeText(textToCopy);
  }
}

export interface Landkoder {
  text: string;
  code: string;
}
